/**
 * Adaptive Quality System
 * リアルタイムパフォーマンス監視に基づく動的品質調整システム
 */

import * as THREE from 'three'
import { webglLogger } from '@/lib/utils/logger'

export interface QualityLevel {
  id: 'ultra' | 'high' | 'medium' | 'low' | 'minimal'
  name: string
  description: string
  settings: QualitySettings
}

export interface QualitySettings {
  /** レンダリング解像度倍率 (0.1-2.0) */
  resolutionScale: number
  /** ピクセル比制限 */
  pixelRatioLimit: number
  /** 最大パーティクル数 */
  maxParticles: number
  /** シャドウ品質 */
  shadowMapSize: number
  /** アンチエイリアス有効 */
  enableAntialiasing: boolean
  /** ポストプロセッシング有効 */
  enablePostProcessing: boolean
  /** ブルーム効果有効 */
  enableBloom: boolean
  /** リフレクション有効 */
  enableReflections: boolean
  /** テクスチャフィルタリング */
  textureFiltering: THREE.TextureFilter
  /** アニメーション品質 (0.1-1.0) */
  animationQuality: number
  /** LODバイアス */
  lodBias: number
}

export interface PerformanceMetrics {
  /** 現在のFPS */
  fps: number
  /** フレーム時間 (ms) */
  frameTime: number
  /** GPU使用率 (0-1) */
  gpuUtilization: number
  /** メモリ使用量 (MB) */
  memoryUsage: number
  /** 描画コール数 */
  drawCalls: number
  /** 三角形数 */
  triangles: number
  /** テクスチャメモリ (MB) */
  textureMemory: number
}

export interface DeviceCapabilities {
  /** GPU層 (1=低性能, 2=中性能, 3=高性能) */
  gpuTier: 1 | 2 | 3
  /** 最大テクスチャサイズ */
  maxTextureSize: number
  /** 最大レンダーバッファサイズ */
  maxRenderbufferSize: number
  /** 利用可能メモリ (MB) */
  availableMemory: number
  /** WebGL2対応 */
  supportsWebGL2: boolean
  /** 浮動小数点テクスチャ対応 */
  supportsFloatTextures: boolean
  /** インスタンスレンダリング対応 */
  supportsInstancing: boolean
  /** モバイルデバイス */
  isMobile: boolean
}

/**
 * 品質レベル定義
 */
export const QUALITY_LEVELS: QualityLevel[] = [
  {
    id: 'ultra',
    name: 'Ultra',
    description: '最高品質 - ハイエンドGPU推奨',
    settings: {
      resolutionScale: 1.5,
      pixelRatioLimit: 3.0,
      maxParticles: 1000000,
      shadowMapSize: 4096,
      enableAntialiasing: true,
      enablePostProcessing: true,
      enableBloom: true,
      enableReflections: true,
      textureFiltering: THREE.LinearMipmapLinearFilter,
      animationQuality: 1.0,
      lodBias: 0.0
    }
  },
  {
    id: 'high',
    name: 'High',
    description: '高品質 - 推奨設定',
    settings: {
      resolutionScale: 1.0,
      pixelRatioLimit: 2.0,
      maxParticles: 500000,
      shadowMapSize: 2048,
      enableAntialiasing: true,
      enablePostProcessing: true,
      enableBloom: true,
      enableReflections: false,
      textureFiltering: THREE.LinearMipmapLinearFilter,
      animationQuality: 1.0,
      lodBias: 0.0
    }
  },
  {
    id: 'medium',
    name: 'Medium',
    description: '標準品質 - バランス重視',
    settings: {
      resolutionScale: 0.8,
      pixelRatioLimit: 1.5,
      maxParticles: 250000,
      shadowMapSize: 1024,
      enableAntialiasing: false,
      enablePostProcessing: true,
      enableBloom: false,
      enableReflections: false,
      textureFiltering: THREE.LinearFilter,
      animationQuality: 0.8,
      lodBias: 0.5
    }
  },
  {
    id: 'low',
    name: 'Low',
    description: '低品質 - パフォーマンス優先',
    settings: {
      resolutionScale: 0.6,
      pixelRatioLimit: 1.0,
      maxParticles: 100000,
      shadowMapSize: 512,
      enableAntialiasing: false,
      enablePostProcessing: false,
      enableBloom: false,
      enableReflections: false,
      textureFiltering: THREE.LinearFilter,
      animationQuality: 0.6,
      lodBias: 1.0
    }
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: '最低品質 - 低性能デバイス向け',
    settings: {
      resolutionScale: 0.4,
      pixelRatioLimit: 1.0,
      maxParticles: 10000,
      shadowMapSize: 256,
      enableAntialiasing: false,
      enablePostProcessing: false,
      enableBloom: false,
      enableReflections: false,
      textureFiltering: THREE.NearestFilter,
      animationQuality: 0.4,
      lodBias: 2.0
    }
  }
]

/**
 * アダプティブ品質システム
 */
export class AdaptiveQualitySystem {
  private currentQuality: QualityLevel
  private targetFPS: number = 60
  private performanceHistory: PerformanceMetrics[] = []
  private historySize: number = 30 // 0.5秒分 (60fps時)
  private adjustmentCooldown: number = 2000 // 2秒
  private lastAdjustment: number = 0
  private autoAdjustment: boolean = true
  private deviceCapabilities: DeviceCapabilities
  private callbacks: Map<string, (quality: QualityLevel) => void> = new Map()

  constructor(
    deviceCapabilities: DeviceCapabilities,
    initialQuality?: QualityLevel['id']
  ) {
    this.deviceCapabilities = deviceCapabilities
    this.currentQuality = this.determineInitialQuality(initialQuality)
    
    webglLogger.info('[AdaptiveQualitySystem] Initialized', {
      deviceCapabilities,
      initialQuality: this.currentQuality.id
    })
  }

  /**
   * 初期品質レベルを決定
   */
  private determineInitialQuality(override?: QualityLevel['id']): QualityLevel {
    if (override) {
      const quality = QUALITY_LEVELS.find(q => q.id === override)
      if (quality) return quality
    }

    // デバイス性能に基づいて自動決定
    if (this.deviceCapabilities.isMobile) {
      return QUALITY_LEVELS.find(q => q.id === 'low')!
    }

    switch (this.deviceCapabilities.gpuTier) {
      case 3:
        return QUALITY_LEVELS.find(q => q.id === 'high')!
      case 2:
        return QUALITY_LEVELS.find(q => q.id === 'medium')!
      case 1:
      default:
        return QUALITY_LEVELS.find(q => q.id === 'low')!
    }
  }

  /**
   * パフォーマンス指標を更新
   */
  public updatePerformanceMetrics(metrics: PerformanceMetrics): void {
    this.performanceHistory.push({
      ...metrics,
      timestamp: performance.now()
    } as PerformanceMetrics & { timestamp: number })

    // 履歴サイズを制限
    if (this.performanceHistory.length > this.historySize) {
      this.performanceHistory.shift()
    }

    // 自動調整を実行
    if (this.autoAdjustment) {
      this.evaluateAndAdjust()
    }
  }

  /**
   * パフォーマンスを評価して品質を調整
   */
  private evaluateAndAdjust(): void {
    const now = performance.now()
    
    // クールダウン期間中はスキップ
    if (now - this.lastAdjustment < this.adjustmentCooldown) {
      return
    }

    // 十分なデータが蓄積されていない場合はスキップ
    if (this.performanceHistory.length < 10) {
      return
    }

    const recentMetrics = this.performanceHistory.slice(-10)
    const avgFPS = recentMetrics.reduce((sum, m) => sum + m.fps, 0) / recentMetrics.length
    const avgFrameTime = recentMetrics.reduce((sum, m) => sum + m.frameTime, 0) / recentMetrics.length
    const avgMemory = recentMetrics.reduce((sum, m) => sum + m.memoryUsage, 0) / recentMetrics.length

    const currentIndex = QUALITY_LEVELS.findIndex(q => q.id === this.currentQuality.id)
    
    // パフォーマンスが低い場合は品質を下げる
    if (avgFPS < this.targetFPS * 0.8 || avgFrameTime > 20 || avgMemory > 800) {
      if (currentIndex < QUALITY_LEVELS.length - 1) {
        this.setQualityLevel(QUALITY_LEVELS[currentIndex + 1].id)
        webglLogger.info('[AdaptiveQualitySystem] Quality decreased due to performance', {
          reason: { avgFPS, avgFrameTime, avgMemory },
          newQuality: this.currentQuality.id
        })
      }
    }
    // パフォーマンスが十分な場合は品質を上げる
    else if (avgFPS > this.targetFPS * 0.95 && avgFrameTime < 12 && avgMemory < 400) {
      if (currentIndex > 0) {
        this.setQualityLevel(QUALITY_LEVELS[currentIndex - 1].id)
        webglLogger.info('[AdaptiveQualitySystem] Quality increased due to good performance', {
          reason: { avgFPS, avgFrameTime, avgMemory },
          newQuality: this.currentQuality.id
        })
      }
    }
  }

  /**
   * 品質レベルを設定
   */
  public setQualityLevel(qualityId: QualityLevel['id']): void {
    const newQuality = QUALITY_LEVELS.find(q => q.id === qualityId)
    if (!newQuality) {
      webglLogger.warn('[AdaptiveQualitySystem] Invalid quality level:', qualityId)
      return
    }

    const oldQuality = this.currentQuality
    this.currentQuality = newQuality
    this.lastAdjustment = performance.now()

    // コールバックを実行
    this.callbacks.forEach(callback => {
      try {
        callback(this.currentQuality)
      } catch (error) {
        webglLogger.error('[AdaptiveQualitySystem] Callback error:', error)
      }
    })

    webglLogger.info('[AdaptiveQualitySystem] Quality changed', {
      from: oldQuality.id,
      to: this.currentQuality.id,
      settings: this.currentQuality.settings
    })
  }

  /**
   * 現在の品質レベルを取得
   */
  public getCurrentQuality(): QualityLevel {
    return this.currentQuality
  }

  /**
   * 現在の品質設定を取得
   */
  public getCurrentSettings(): QualitySettings {
    return { ...this.currentQuality.settings }
  }

  /**
   * 自動調整の有効/無効を設定
   */
  public setAutoAdjustment(enabled: boolean): void {
    this.autoAdjustment = enabled
    webglLogger.info('[AdaptiveQualitySystem] Auto adjustment:', enabled)
  }

  /**
   * ターゲットFPSを設定
   */
  public setTargetFPS(fps: number): void {
    this.targetFPS = Math.max(30, Math.min(120, fps))
    webglLogger.info('[AdaptiveQualitySystem] Target FPS set to:', this.targetFPS)
  }

  /**
   * 品質変更コールバックを登録
   */
  public onQualityChange(id: string, callback: (quality: QualityLevel) => void): void {
    this.callbacks.set(id, callback)
  }

  /**
   * 品質変更コールバックを削除
   */
  public removeQualityChangeListener(id: string): void {
    this.callbacks.delete(id)
  }

  /**
   * パフォーマンス統計を取得
   */
  public getPerformanceStats(): {
    current: PerformanceMetrics | null
    average: PerformanceMetrics | null
    quality: QualityLevel
  } {
    const current = this.performanceHistory[this.performanceHistory.length - 1] || null
    
    let average = null
    if (this.performanceHistory.length > 0) {
      const recent = this.performanceHistory.slice(-30)
      average = {
        fps: recent.reduce((sum, m) => sum + m.fps, 0) / recent.length,
        frameTime: recent.reduce((sum, m) => sum + m.frameTime, 0) / recent.length,
        gpuUtilization: recent.reduce((sum, m) => sum + m.gpuUtilization, 0) / recent.length,
        memoryUsage: recent.reduce((sum, m) => sum + m.memoryUsage, 0) / recent.length,
        drawCalls: recent.reduce((sum, m) => sum + m.drawCalls, 0) / recent.length,
        triangles: recent.reduce((sum, m) => sum + m.triangles, 0) / recent.length,
        textureMemory: recent.reduce((sum, m) => sum + m.textureMemory, 0) / recent.length
      }
    }

    return { current, average, quality: this.currentQuality }
  }

  /**
   * システムを破棄
   */
  public dispose(): void {
    this.callbacks.clear()
    this.performanceHistory.length = 0
    webglLogger.info('[AdaptiveQualitySystem] Disposed')
  }
}

/**
 * デバイス性能を分析
 */
export async function analyzeDeviceCapabilities(): Promise<DeviceCapabilities> {
  // キャンバスを作成してWebGLコンテキストを取得
  const canvas = document.createElement('canvas')
  const gl = canvas.getContext('webgl2') || canvas.getContext('webgl')
  
  if (!gl) {
    throw new Error('WebGL not supported')
  }

  const capabilities: DeviceCapabilities = {
    gpuTier: 2, // デフォルト値
    maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
    maxRenderbufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),
    availableMemory: (navigator as Navigator & { deviceMemory?: number }).deviceMemory ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory! * 1024 : 4096,
    supportsWebGL2: !!canvas.getContext('webgl2'),
    supportsFloatTextures: !!gl.getExtension('OES_texture_float'),
    supportsInstancing: !!gl.getExtension('ANGLE_instanced_arrays'),
    isMobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  }

  // GPU性能を簡易的に測定
  const renderer = gl.getParameter(gl.RENDERER)
  const vendor = gl.getParameter(gl.VENDOR)
  
  // GPU層を判定（簡易版）
  const gpuInfo = `${vendor} ${renderer}`.toLowerCase()
  if (gpuInfo.includes('nvidia') && (gpuInfo.includes('rtx') || gpuInfo.includes('gtx'))) {
    capabilities.gpuTier = 3
  } else if (gpuInfo.includes('amd') || gpuInfo.includes('intel iris')) {
    capabilities.gpuTier = 2
  } else if (capabilities.isMobile || gpuInfo.includes('intel hd')) {
    capabilities.gpuTier = 1
  }

  // クリーンアップ
  canvas.remove()

  webglLogger.info('[DeviceCapabilities] Analysis complete', capabilities)
  return capabilities
}